/**
 * CloudModule — IModule for volumetric clouds.
 *
 * Generates the 3D noise + weather textures at init, builds a
 * `CloudRenderer`, registers a runtime system that drives per-frame sun
 * direction + coverage, and exposes a `compositeNode` getter that the
 * user can plug into their post-processing chain.
 *
 * Integration with the existing PostProcessingPipeline is explicit rather
 * than automatic — the user calls:
 *
 *   const cloudModule = app.getModule('Clouds') as CloudModule;
 *   const cloudNode = cloudModule.renderer.buildCompositeNode();
 *   // then compose with scene color in their own TSL chain.
 *
 * Automatic integration via the pipeline's `clouds` config key is also
 * possible but requires PostProcessingPipeline to be rebuilt on cloud
 * module init — that's a v2 refinement.
 *
 * Default integration: if `WeatherSystem` is registered, coverage flows
 * from `WeatherSystem.getCloudCoverage()`. If `SkyModule` is registered,
 * sun direction flows from its `sunDirectionProvider`. Otherwise sensible
 * defaults are used.
 */

import type { App, IModule } from '../../core/App.js';
import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import type * as THREE from 'three/webgpu';
import { Vector3 } from 'three/webgpu';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { Logger } from '../../core/Logger.js';
import {
  createCloudConfig, CLOUD_PRESETS,
  type CloudConfig,
} from './CloudConfig.js';
import { CloudRenderer, combineCloudLayers } from './CloudRenderer.js';
import { CloudMeshField, type CloudMeshFieldConfig } from './CloudMeshField.js';
import { CloudImpostorLayer } from './CloudImpostorLayer.js';
import { initBlueNoiseCached, prefetchBlueNoiseCache } from '../bluenoise/BlueNoiseRuntime.js';
import { generateCloudNoiseCached, prefetchCloudNoiseCache, type CloudNoiseTextures } from './CloudNoiseGenerator.js';
import { defineXClouds, registerCloudsHtml } from './html/index.js';

export interface CloudModuleConfig {
  /** Cloud-rendering config. Partial overrides the defaults. */
  cloud?: Partial<CloudConfig>;
  /** Preset selector (sets stepping parameters). Default 'medium'. */
  qualityPreset?: keyof typeof CLOUD_PRESETS;
  /**
   * Optional sun-direction provider — world-space unit vec FROM origin
   * TO sun. If omitted and SkyModule is present, the cloud system reads
   * from SkyModule's sun via its introspection getter.
   */
  sunDirectionProvider?: () => { x: number; y: number; z: number };
  /**
   * Optional coverage driver. If omitted, starts at config.coverage and
   * can be changed via setCoverage().
   */
  coverageProvider?: () => number;
  /**
   * Disable the legacy `CloudLayer` sphere-dome renderer when this module
   * is active. Defaults true — they would visually conflict.
   */
  disableLegacyDome?: boolean;
  /**
   * Optional second, LOW scattered cloud layer (presence enables it).
   * WHY: a high deck (2-10 km) is world-anchored but parallaxes only
   * ~atan(d/bottomAltitude) as the player moves — it perceptually "follows"
   * the camera. A thin low layer (~800-1500 m) of small puffs swings ~4° per
   * 50 m walked, making the world-anchoring legible (you can walk under and
   * past a cloud). Rendered as a second CloudRenderer sharing the baked
   * noise textures; compose via `buildCompositeNode()` (low OVER high).
   */
  lowLayer?: CloudLowLayerConfig;
  /**
   * Mesh-cloud field (presence enables it): clouds as REAL 3D meshes in the
   * world (pooled sphere volumes, per-fragment raymarch of the shared baked
   * noise inside each volume) instead of / alongside the screen-space slab.
   * WHY: the post-pass slab is a screen-space effect — even perfectly
   * world-anchored, a high deck's parallax at walking speed is below the
   * perception threshold, so players read it as "painted on the screen".
   * Mesh clouds parallax, occlude, and cull like any other world geometry.
   * The app decides whether to ALSO keep the post-pass composite (atlas:
   * mesh field replaces it — see PostModule).
   */
  meshField?: CloudMeshFieldConfig;
}

export interface CloudLowLayerConfig {
  /**
   * Partial config for the low layer. Defaults: 800-1500 m slab,
   * coverage 0.22, noiseScale 0.0012 (~300-500 m puffs), weatherScale
   * 0.00025 (4 km clusters); wind inherits the main layer unless overridden.
   */
  cloud?: Partial<CloudConfig>;
  /** March quality for the low layer. Default 'low' (thin slab, few steps). */
  qualityPreset?: keyof typeof CLOUD_PRESETS;
}

/** Low-layer defaults — see CloudLowLayerConfig. */
const LOW_LAYER_DEFAULTS = {
  bottomAltitude: 800,
  topAltitude: 1500,
  coverage: 0.22,
  noiseScale: 0.0012,
  weatherScale: 0.00025,
} as const;

export class CloudModule implements IModule {
  readonly name = 'Clouds';

  private _config: CloudModuleConfig;
  private _cloudConfig: CloudConfig;
  private _renderer: CloudRenderer | null = null;
  private _lowRenderer: CloudRenderer | null = null;
  private _meshField: CloudMeshField | null = null;
  /** The far LOD of the mesh field (instanced sprites to the horizon). */
  private _impostors: CloudImpostorLayer | null = null;
  private _lowCoverageRatio = 1;
  private _noise: CloudNoiseTextures | null = null;
  private _system: CloudRuntimeSystem | null = null;
  /** dispose() raced an in-flight init() (two bake-cache awaits, up to 600 ms each): the continuation must stop. */
  private _disposed = false;

  constructor(config: CloudModuleConfig = {}) {
    // Phase 0: kick the bake-cache reads off NOW — init() runs much later
    // (late module init) and finds the bytes already waiting.
    prefetchCloudNoiseCache();
    prefetchBlueNoiseCache();
    this._config = config;
    const preset = config.qualityPreset ?? 'medium';
    this._cloudConfig = createCloudConfig({
      ...config.cloud,
      quality: CLOUD_PRESETS[preset],
    });
  }

  /** The weather map (R = coverage) the raymarch samples — shared with cloud-shadow layers (stage 4). */
  get weatherMap(): THREE.DataTexture | null { return this._noise?.weather ?? null; }

  get renderer(): CloudRenderer {
    if (!this._renderer) throw new Error('CloudModule: not initialized');
    return this._renderer;
  }

  /** The optional low scattered layer's renderer (null when not configured). */
  get lowRenderer(): CloudRenderer | null { return this._lowRenderer; }

  /**
   * The mesh-cloud field (null when not configured). Exposes the same
   * lighting-driver surface as CloudRenderer (`uniforms.sunIlluminance/…`,
   * setSunColor, setDensityScale) so weather/night ramps drive it unchanged.
   */
  get meshField(): CloudMeshField | null { return this._meshField; }

  /** True when a mesh field is configured AND currently enabled. */
  get meshFieldActive(): boolean { return !!this._meshField && this._meshField.enabled; }

  /** Enable/disable the mesh field at runtime (tier gating). No-op when
   *  no meshField is configured. */
  setMeshFieldEnabled(on: boolean): void {
    this._meshField?.setEnabled(on);
    this._impostors?.setEnabled(on);
  }

  /** The mesh field's far LOD (null when no mesh field, or `impostors: false`). */
  get impostorLayer(): CloudImpostorLayer | null { return this._impostors; }

  get config(): CloudConfig { return this._cloudConfig; }

  /**
   * Composite node for the whole cloud system: the low layer OVER the high
   * deck when a `lowLayer` is configured, else the high deck's node alone.
   * Same `{rgb, a}` contract as `renderer.buildCompositeNode()`.
   */
  buildCompositeNode(): unknown {
    const high = this.renderer.buildCompositeNode();
    if (!this._lowRenderer) return high;
    return combineCloudLayers(this._lowRenderer.buildCompositeNode(), high);
  }

  /** Runtime coverage update (drives the low layer at its coverage ratio). */
  setCoverage(v: number): void {
    this._cloudConfig.coverage = Math.max(0, Math.min(1, v));
    this._renderer?.setCoverage(this._cloudConfig.coverage);
    this._lowRenderer?.setCoverage(this._cloudConfig.coverage * this._lowCoverageRatio);
  }

  /**
   * Switch the raymarch quality preset. Step counts are compile-time
   * constants in the TSL graph, so this invalidates the memoized node —
   * the caller must rebuild whatever pipeline consumes
   * `renderer.buildCompositeNode()` for the change to take effect (the
   * app-level post module already rebuilds its pipeline on tier change).
   */
  setQualityPreset(preset: keyof typeof CLOUD_PRESETS): void {
    this._cloudConfig.quality = CLOUD_PRESETS[preset];
    this._renderer?.invalidateNode();
    // The low layer's preset is set separately (setLowQualityPreset) — its
    // memoized node survives; only the combined node's HIGH child changes.
  }

  /**
   * March quality for the LOW layer. Near puffs show step/noise grain far
   * more than the distant deck (a 40 m 'low' step across a 300 m puff at
   * 800 m altitude is visibly chunky), so apps typically run the low layer
   * one preset FINER than they would afford for the whole sky. No-op
   * without a low layer. Requires a pipeline rebuild to take effect (step
   * counts are compile-time constants in the node graph).
   */
  setLowQualityPreset(preset: keyof typeof CLOUD_PRESETS): void {
    if (!this._lowRenderer) return;
    this._lowRenderer.config.quality = CLOUD_PRESETS[preset];
    this._lowRenderer.invalidateNode();
  }

  /**
   * Camera-translation exaggeration for BOTH layers (live uniform, no
   * rebuild). 1 = physically correct; 2-4 = game-feel "clouds stream past
   * as I walk". See CloudConfig.parallaxScale.
   */
  setParallaxScale(v: number): void {
    this._renderer?.setParallaxScale(v);
    this._lowRenderer?.setParallaxScale(v);
  }

  /** Runtime sun direction update (world-space, FROM origin TO sun). */
  setSunDirection(dir: { x: number; y: number; z: number }): void {
    this._renderer?.setSunDirection(dir);
    this._lowRenderer?.setSunDirection(dir);
  }

  /** Runtime sun color update. */
  setSunColor(color: { r: number; g: number; b: number }): void {
    this._renderer?.setSunColor(color);
    this._lowRenderer?.setSunColor(color);
  }

  async init(app: App): Promise<void> {
    Logger.info('Clouds', 'Generating procedural 3D noise textures …');
    const t0 = performance.now();
    // Phase 0 (bake cache): a repeat boot restores the identical bytes from
    // IndexedDB instead of paying the ~0.5 s bake; any cache trouble bakes.
    const noise = await generateCloudNoiseCached();
    this._noise = noise.textures;
    // WHY: a teardown during the bake await used to be overtaken — init kept
    // going, added groups to the scene and registered a live system on a
    // disposed module (2026-09-12).
    if (this._disposed) { this._disposeNoise(); return; }
    Logger.info('Clouds',
      `Noise ${noise.source === 'cache' ? 'restored from cache' : 'baked'} in ${(performance.now() - t0).toFixed(0)}ms ` +
      '(64³ base, 32³ detail, 512² weather)');

    // Blue-noise march jitter (idempotent global init, ~100 ms once, cached
    // the same way) — must exist BEFORE buildCompositeNode() runs: the
    // sampler binds the texture at node-build time, and without it the march
    // falls back to the white-noise hash (grainy cloud edges).
    await initBlueNoiseCached();
    if (this._disposed) { this._disposeNoise(); return; }

    this._renderer = new CloudRenderer({
      config: this._cloudConfig,
      baseNoise: this._noise.base,
      detailNoise: this._noise.detail,
      weatherMap: this._noise.weather,
    });

    if (this._config.lowLayer) {
      const ll = this._config.lowLayer;
      const lowConfig = createCloudConfig({
        ...LOW_LAYER_DEFAULTS,
        // Wind inherits the main layer so the sky drifts as one system.
        windX: this._cloudConfig.windX,
        windZ: this._cloudConfig.windZ,
        windTimeScale: this._cloudConfig.windTimeScale,
        ...ll.cloud,
        quality: CLOUD_PRESETS[ll.qualityPreset ?? 'low'],
      });
      // Dynamic (weather-driven) coverage scales the low layer by the ratio
      // of the two BASE coverages, so storms thicken and clear skies clear
      // both layers proportionally.
      this._lowCoverageRatio =
        lowConfig.coverage / Math.max(0.01, this._cloudConfig.coverage);
      this._lowRenderer = new CloudRenderer({
        config: lowConfig,
        baseNoise: this._noise.base,
        detailNoise: this._noise.detail,
        weatherMap: this._noise.weather,
      });
      Logger.info('Clouds',
        `Low scattered layer enabled (${lowConfig.bottomAltitude}-` +
        `${lowConfig.topAltitude} m, coverage ${lowConfig.coverage.toFixed(2)}, ` +
        `preset ${ll.qualityPreset ?? 'low'})`);
    }

    if (this._config.meshField) {
      this._meshField = new CloudMeshField({
        config: {
          // Wind/evolution inherit the main layer so the sky moves as one.
          windX: this._cloudConfig.windX,
          windZ: this._cloudConfig.windZ,
          windTimeScale: this._cloudConfig.windTimeScale,
          evolveSpeed: this._cloudConfig.evolveSpeed,
          ...this._config.meshField,
        },
        baseNoise: this._noise.base,
        detailNoise: this._noise.detail,
        weatherMap: this._noise.weather,
      });
      this._meshField.setCoverage(this._cloudConfig.coverage);
      app.world.scene.add(this._meshField.group);
      Logger.info('Clouds',
        `Mesh-cloud field enabled (${this._config.meshField.maxClouds ?? 40} ` +
        `cloud pool, radius ${this._config.meshField.fieldRadius ?? 9000} m)`);
      // The far LOD: the same lattice as sprites out to the horizon.
      if (this._config.meshField.impostors !== false) {
        this._impostors = new CloudImpostorLayer({ field: this._meshField, config: this._config.meshField.impostors ?? {} });
        app.world.scene.add(this._impostors.group);
        Logger.info('Clouds',
          `Cloud impostor layer enabled (${this._impostors.config.maxSprites} sprites to ${this._impostors.config.farRadius} m)`);
      }
    }

    this._system = new CloudRuntimeSystem({
      app,
      renderer: this._renderer,
      lowRenderer: this._lowRenderer,
      meshField: this._meshField,
      impostors: this._impostors,
      lowCoverageRatio: this._lowCoverageRatio,
      sunProvider: this._config.sunDirectionProvider ?? null,
      coverageProvider: this._config.coverageProvider ?? null,
    });
    app.world.addSystem(this._system);

    if (this._config.disableLegacyDome !== false) {
      this._tryDisableLegacyDome(app);
    }

    defineXClouds();
    const htmlModule = app.getModule('HtmlEcs') as
      | (IModule & { htmlRegistry?: unknown }) | undefined;
    if (htmlModule && htmlModule.htmlRegistry) {
      registerCloudsHtml(htmlModule.htmlRegistry as never, this);
      Logger.info('Clouds',
        'HTML integration wired (<x-clouds> + clouds.* comp keys)');
    }

    Logger.info('Clouds',
      `Module initialized (quality=${this._config.qualityPreset ?? 'medium'}, ` +
      `coverage=${this._cloudConfig.coverage.toFixed(2)})`);
  }

  dispose(): void {
    this._disposed = true;
    this._renderer?.dispose();
    this._renderer = null;
    this._lowRenderer?.dispose();
    this._lowRenderer = null;
    this._impostors?.dispose();
    this._impostors = null;
    this._meshField?.dispose();
    this._meshField = null;
    this._disposeNoise();
    this._system = null;
    Logger.info('Clouds', 'Module disposed');
  }

  /** The module owns the shared noise bake (renderers no longer dispose it). */
  private _disposeNoise(): void {
    if (this._noise) {
      this._noise.base.dispose();
      this._noise.detail.dispose();
      this._noise.weather.dispose();
    }
    this._noise = null;
  }

  private _tryDisableLegacyDome(app: App): void {
    const weather = app.getModule('Weather') as
      | (IModule & { cloudLayer?: { mesh?: { visible: boolean } } })
      | undefined;
    const mesh = weather?.cloudLayer?.mesh;
    if (mesh) {
      mesh.visible = false;
      Logger.info('Clouds',
        'Disabled legacy CloudLayer sphere-dome (volumetric clouds replace it)');
    }
  }
}

/**
 * CloudRuntimeSystem — feeds sun direction, coverage, wind to the renderer
 * each frame. Priority RenderSub/5 — runs before the post-processing stage.
 */
/** Scratch camera position (no per-frame allocation). */
const _tmpCamPos = new Vector3();

class CloudRuntimeSystem implements ISystem {
  readonly name = 'CloudRuntime';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.Update;
  readonly priority = 5;
  private readonly _renderer: CloudRenderer;
  private readonly _lowRenderer: CloudRenderer | null;
  private readonly _meshField: CloudMeshField | null;
  private readonly _impostors: CloudImpostorLayer | null;
  private readonly _lowCoverageRatio: number;
  private readonly _sunProvider: (() => { x: number; y: number; z: number }) | null;
  private readonly _coverageProvider: (() => number) | null;
  private readonly _app: App;

  constructor(args: {
    app: App;
    renderer: CloudRenderer;
    lowRenderer: CloudRenderer | null;
    meshField: CloudMeshField | null;
    impostors: CloudImpostorLayer | null;
    lowCoverageRatio: number;
    sunProvider: (() => { x: number; y: number; z: number }) | null;
    coverageProvider: (() => number) | null;
  }) {
    this._app = args.app;
    this._renderer = args.renderer;
    this._lowRenderer = args.lowRenderer;
    this._meshField = args.meshField;
    this._impostors = args.impostors;
    this._lowCoverageRatio = args.lowCoverageRatio;
    this._sunProvider = args.sunProvider;
    this._coverageProvider = args.coverageProvider;
  }

  execute(dt: number, _world: World): void {
    // Advance wind scroll.
    this._renderer.tick(dt);
    this._lowRenderer?.tick(dt);

    // Origin-rebase compensation: large-world games shift scene.position to
    // keep render coordinates small; feed it so the cloud noise field stays
    // anchored to ABSOLUTE world space across rebase snaps.
    const sp = this._app.world.scene.position;
    (this._renderer.uniforms.worldOffset.value as THREE.Vector2).set(sp.x, sp.z);
    if (this._lowRenderer) {
      (this._lowRenderer.uniforms.worldOffset.value as THREE.Vector2).set(sp.x, sp.z);
    }

    // Explicit camera state — the composite node runs in the post-quad
    // context where ambient TSL camera accessors bind to the wrong camera.
    // (The MESH field doesn't have that problem — it renders in the scene
    // pass — but it still needs the camera position for its cell streamer.)
    try {
      const cam = this._app.renderer.cameras.main;
      if (cam) {
        this._renderer.setCameraState(cam);
        this._lowRenderer?.setCameraState(cam);
        if (this._meshField?.enabled) {
          _tmpCamPos.setFromMatrixPosition(cam.matrixWorld);
          this._meshField.update(dt, _tmpCamPos, this._app.world.scene.position);
          if (this._impostors?.enabled) {
            this._impostors.update(dt, _tmpCamPos, this._app.world.scene.position);
            // Aerial perspective straight from the scene's exponential fog.
            const fog = this._app.world.scene.fog as { isFogExp2?: boolean; color: { r: number; g: number; b: number }; density: number } | null;
            if (fog?.isFogExp2) this._impostors.setHaze(fog.color, fog.density);
          }
        }
      }
    } catch { /* no camera yet */ }

    // Sun direction: explicit provider > SkyModule's sun > noop.
    if (this._sunProvider) {
      const sun = this._sunProvider();
      this._renderer.setSunDirection(sun);
      this._lowRenderer?.setSunDirection(sun);
      this._meshField?.setSunDirection(sun);
    } else {
      const sky = this._app.getModule('Sky') as
        | (IModule & { _config?: { sunDirectionProvider?: () => THREE.Vector3 } })
        | undefined;
      const provider = (sky as { _config?: { sunDirectionProvider?: () => THREE.Vector3 } } | undefined)
        ?._config?.sunDirectionProvider;
      if (provider) {
        const dir = provider();
        this._renderer.setSunDirection({ x: dir.x, y: dir.y, z: dir.z });
        this._lowRenderer?.setSunDirection({ x: dir.x, y: dir.y, z: dir.z });
        this._meshField?.setSunDirection({ x: dir.x, y: dir.y, z: dir.z });
      }
    }

    // Coverage: explicit provider > WeatherSystem > leave at config default.
    // The low layer follows at its base-coverage ratio so storms thicken and
    // clear skies clear both layers proportionally.
    let cov: number | null = null;
    if (this._coverageProvider) {
      cov = this._coverageProvider();
    } else {
      const weather = this._app.getModule('Weather') as
        | (IModule & { getCloudCoverage?: () => number })
        | undefined;
      if (weather?.getCloudCoverage) cov = weather.getCloudCoverage();
    }
    if (cov !== null) {
      this._renderer.setCoverage(cov);
      this._lowRenderer?.setCoverage(cov * this._lowCoverageRatio);
      // The mesh field takes the full coverage (it IS the near layer — its
      // spawn threshold + in-volume gate are driven directly).
      this._meshField?.setCoverage(cov);
    }
  }
}
